/**
 * Calculate the ISO week number for a given date
 * @param date The date to get the week number for
 * @returns The ISO week number (1-53)
 *
 * src\utils\dateUtils.ts
 *
 */
import { getWeek } from "date-fns";
import { formatDateWithLocale } from "./calendar";

export function getWeekNumber(date: Date): number {
	return getWeek(date, { weekStartsOn: 6 });
}

/**
 * Date format types for localized formatting
 */
export type DateFormatType = 'full' | 'short' | 'monthDay' | 'monthYear' | 'fullWithTime';

/**
 * Get the appropriate format string based on locale and format type
 */
function getFormatString(formatType: DateFormatType, locale: string): string {
	const formats: Record<DateFormatType, Record<string, string>> = {
		full: {
			en: "MMMM d, yyyy",
			fr: "d MMMM yyyy",
			nl: "d MMMM yyyy"
		},
		short: {
			en: "MMM d, yyyy",
			fr: "d MMM yyyy",
			nl: "d MMM yyyy"
		},
		monthDay: {
			en: "MMM d",
			fr: "d MMM",
			nl: "d MMM"
		},
		monthYear: {
			en: "MMMM yyyy",
			fr: "MMMM yyyy",
			nl: "MMMM yyyy"
		},
		fullWithTime: {
			en: "MMMM d, yyyy 'at' h:mm a",
			fr: "d MMMM yyyy 'à' HH:mm",
			nl: "d MMMM yyyy 'om' HH:mm"
		}
	};
	
	return formats[formatType][locale] || formats[formatType]['en'];
}

/**
 * Format a date string or Date object with localization
 * @param date The date to format (string in YYYY-MM-DD format or Date object)
 * @param locale The locale (en, fr, nl)
 * @param formatType The type of format to use (default: 'full')
 * @returns Formatted date string
 */
export function formatLocalizedDate(
	date: string | Date, 
	locale: string,
	formatType: DateFormatType = 'full'
): string {
	const dateObj = typeof date === 'string' ? new Date(date) : date;
	const formatString = getFormatString(formatType, locale);
	return formatDateWithLocale(dateObj, formatString, locale);
}


export const isMonday = (date: Date) => date.getDay() === 1; // 1 = Monday
export const isTuesday = (date: Date) => date.getDay() === 2; // 2 = Tuesday	
export const isWednesday = (date: Date) => date.getDay() === 3; // 3 = Wednesday
export const isThursday = (date: Date) => date.getDay() === 4; // 4 = Thursday
export const isFriday = (date: Date) => date.getDay() === 5; // 5 = Friday
export const isSaturday = (date: Date) => date.getDay() === 6; // 6 = Saturday
export const isSunday = (date: Date) => date.getDay() === 0; // 0 = Sunday
export const isWeekend = (date: Date) => date.getDay() === 0 || date.getDay() === 6; // 0 = Sunday, 6 = Saturday
export const isWeekday = (date: Date) => date.getDay() !== 0 && date.getDay() !== 6; // 0 = Sunday, 6 = Saturday
export const isLastDayOfMonth = (date: Date) => new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate() === date.getDate();
export const isFirstDayOfMonth = (date: Date) => date.getDate() === 1;


export const isStartOfWeek = (date: Date, startOfWeek: string) => {
	switch (startOfWeek) {
		case "Monday":
			return isMonday(date);
		case "Tuesday":
			return isTuesday(date);
		case "Wednesday":
			return isWednesday(date);
		case "Thursday":
			return isThursday(date);
		case "Friday":
			return isFriday(date);
		case "Saturday":
			return isSaturday(date);
		case "Sunday":
			return isSunday(date);
		default:
			throw new Error("Invalid start of week day");
	}
}; // as set in weekStartsOn on (site).json